import type { ThreeEvent } from '@react-three/fiber';
import { useThree } from '@react-three/fiber';
import { partial } from 'lodash-es';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
  animationFrameScheduler,
  filter,
  finalize,
  fromEvent,
  map,
  observeOn,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import type { Mesh, Vector3, Vector3Tuple } from 'three';

import { TOOL_CAMERA_VIEW_MODE } from '@/constant/toolConstant';
import { useSubscriptions } from '@/package';
import { cn } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import { useToolMainCameraStore, useToolMenuStore } from '@/store';
import type { NEW__LabelingBox } from '@/util';
import { computeMouse, getPlanePoint, withCanvasXY, withTarget } from '@/util';

import { setControlEnabled } from './EditingLabelBox/util';
import { useToolPcdCanvasContext } from './ToolPcdCanvas';

const ToolPcdCanvasMouseHandler = ({
  vector,
  onClick,
  onPointerDown,
  onPointerEnter,
  onPointerUp,
  onPointerOut,
  onPointerOver,
  onCreateDragbox,
  onDoubleClick,
}: {
  vector?: Vector3Tuple;
  onClick?(event: ThreeEvent<MouseEvent>): void;
  onPointerDown?(event: ThreeEvent<MouseEvent>): void;
  onPointerEnter?(event: ThreeEvent<MouseEvent>): void;
  onPointerUp?(event: ThreeEvent<MouseEvent>): void;
  onPointerOut?(event: ThreeEvent<MouseEvent>): void;
  onPointerOver?(event: ThreeEvent<MouseEvent>): void;
  onCreateDragbox?(params: {
    cornerPointList: Vector3[];
    canvasPoint: {
      start: { x: number; y: number };
      last: { x: number; y: number };
    };
    dragBoxRect: DOMRect;
  }): void;
  onDoubleClick?(event: ThreeEvent<MouseEvent>): void;
}) => {
  const { enabledDragBox } = useToolPcdCanvasContext();
  const { labelingBoxList } = useLabelingBoxContext();
  const { camera, controls, gl, raycaster } = useThree();
  const toolMap = useToolMenuStore(state => state.toolMap);
  const modeIndex = useToolMainCameraStore(state => state.modeIndex);
  const mode = useMemo(
    () => useToolMainCameraStore.getState().getMode(),
    [modeIndex],
  );
  const labelingBoxListRef = useRef<NEW__LabelingBox[]>([]);
  const controlsRef = useRef(controls);
  const ref = useRef<Mesh>(null);

  const withIntersection = (
    eventHandler: (event: ThreeEvent<MouseEvent>) => void,
    event: ThreeEvent<MouseEvent>,
  ) => {
    eventHandler(
      computeMouse({
        camera,
        event,
        gl,
        targetIntersections:
          labelingBoxListRef.current.map(v => v.boxMesh) ?? [],
      }),
    );
  };

  useSubscriptions(
    () => [
      fromEvent<MouseEvent>(window, 'mousedown')
        .pipe(
          withTarget,
          filter(() => !!controlsRef.current),
          filter(e => {
            return (
              (enabledDragBox === true &&
                e.target!.constructor === HTMLCanvasElement &&
                ((e.ctrlKey && !e.metaKey) || (!e.ctrlKey && e.metaKey)) &&
                !e.altKey &&
                !e.shiftKey) ||
              useToolMenuStore.getState().isSelectedByToolName('aiAssist')
            );
          }),
          withCanvasXY,
          map(e => {
            const target = (e.target as HTMLElement).parentElement;
            const startPoint = {
              x: e.clientX - target!.getBoundingClientRect().x,
              y: e.clientY - target!.getBoundingClientRect().y,
            };
            const startCanvasPoint = {
              x: e.canvasX,
              y: e.canvasY,
            };
            let dragBox = target!.querySelector<HTMLElement>(`.tool-drag-box`);

            if (!dragBox) {
              dragBox = document.createElement('div');
              dragBox.style.width = '0';
              dragBox.style.height = '0';
              dragBox.className = cn(
                'tool-drag-box absolute rounded-sm border-[2.5px] border-gray-200/60 bg-gray-200/30',
              );

              target!.append(dragBox);
            }

            dragBox.style.top = `${startPoint.y}px`;
            dragBox.style.left = `${startPoint.x}px`;

            return { target, startPoint, startCanvasPoint, dragBox };
          }),
          tap(() => setControlEnabled(false, controlsRef.current)),
          switchMap(({ target, startPoint, startCanvasPoint, dragBox }) => {
            const maxX = target!.offsetWidth;
            const maxY = target!.offsetHeight;
            let lastCanvasPoint = { x: 0, y: 0 };

            return fromEvent<MouseEvent>(window, 'mousemove').pipe(
              filter(
                e =>
                  !!target &&
                  ((e.ctrlKey && !e.metaKey) ||
                    (!e.ctrlKey && e.metaKey) ||
                    useToolMenuStore
                      .getState()
                      .isSelectedByToolName('aiAssist')),
              ),
              withTarget,
              withCanvasXY,
              map(e => ({
                lastPoint: {
                  x: e.clientX - target!.getBoundingClientRect().x,
                  y: e.clientY - target!.getBoundingClientRect().y,
                },
                lastCanvasPoint: {
                  x: e.canvasX,
                  y: e.canvasY,
                },
              })),
              tap(({ lastPoint, lastCanvasPoint: _lcp }) => {
                const isNegative = {
                  x: lastPoint.x - startPoint.x < 0,
                  y: lastPoint.y - startPoint.y < 0,
                };
                let [w, h] = [
                  Math.abs(lastPoint.x - startPoint.x),
                  Math.abs(lastPoint.y - startPoint.y),
                ];
                let [tx, ty] = [isNegative.x ? -w : 0, isNegative.y ? -h : 0];

                if (startPoint.x + tx + w > maxX) {
                  w -= startPoint.x + tx + w - maxX;
                } else if (startPoint.x + tx < 0) {
                  w += startPoint.x + tx;
                  tx = -w;
                }

                if (startPoint.y + ty + h > maxY) {
                  h -= startPoint.y + ty + h - maxY;
                } else if (startPoint.y + ty < 0) {
                  h += startPoint.y + ty;
                  ty = -h;
                }

                dragBox.style.width = `${w}px`;
                dragBox.style.height = `${h}px`;
                dragBox.style.transform = `translate(${tx}px, ${ty}px)`;

                lastCanvasPoint = _lcp;
              }),
              takeUntil(
                fromEvent<MouseEvent>(window, 'mouseup').pipe(
                  tap(() => {
                    const dragBoxRect = dragBox.getBoundingClientRect();

                    if (dragBoxRect.width > 5 && dragBoxRect.height > 5) {
                      const canvasRect = gl.domElement.getBoundingClientRect();
                      const toNormalizedCoord = (
                        clientX: number,
                        clientY: number,
                      ) => ({
                        x:
                          ((clientX - canvasRect.left) / canvasRect.width) * 2 -
                          1,
                        y:
                          -((clientY - canvasRect.top) / canvasRect.height) *
                            2 +
                          1,
                      });
                      const cornerList = [
                        toNormalizedCoord(dragBoxRect.left, dragBoxRect.top),
                        toNormalizedCoord(dragBoxRect.right, dragBoxRect.top),
                        toNormalizedCoord(
                          dragBoxRect.right,
                          dragBoxRect.bottom,
                        ),
                        toNormalizedCoord(dragBoxRect.left, dragBoxRect.bottom),
                      ];
                      const cornerPointList = cornerList.map(corner =>
                        getPlanePoint({
                          ...corner,
                          camera: camera!,
                          raycaster: raycaster!,
                          vector,
                        }),
                      );

                      onCreateDragbox?.({
                        cornerPointList,
                        canvasPoint: {
                          start: startCanvasPoint,
                          last: lastCanvasPoint,
                        },
                        dragBoxRect,
                      });
                    }

                    dragBox.style.top = '-1px';
                    dragBox.style.left = '-1px';
                    dragBox.style.width = '0';
                    dragBox.style.height = '0';
                    dragBox.style.transform = `translate(0, 0)`;
                    target = null;
                  }),
                  observeOn(animationFrameScheduler),
                  finalize(() => {
                    setControlEnabled(true, controlsRef.current);
                  }),
                ),
              ),
            );
          }),
          observeOn(animationFrameScheduler),
          finalize(() => {
            document.querySelector(`.tool-drag-box`)?.remove();
            setControlEnabled(true, controlsRef.current);
          }),
        )
        .subscribe(),
    ],
    [camera, raycaster, enabledDragBox, toolMap],
  );

  useLayoutEffect(() => {
    if (!ref.current) return;

    if (mode === TOOL_CAMERA_VIEW_MODE.SIDE) {
      ref.current.rotation.set(Math.PI / 2, 0, 0);
      ref.current.position.set(0, 1000, 0);
      return;
    }

    if (mode === TOOL_CAMERA_VIEW_MODE.FRONT) {
      ref.current.rotation.set(Math.PI / 2, Math.PI / 2, 0);
      ref.current.position.set(-1000, 0, 0);
      return;
    }

    ref.current.rotation.set(0, 0, 0);
    ref.current.position.set(0, 0, 0);
  }, [mode]);

  useEffect(() => {
    controlsRef.current = controls;
  }, [controls]);

  useEffect(() => {
    labelingBoxListRef.current = labelingBoxList;
  }, [labelingBoxList]);

  return (
    <mesh
      ref={ref}
      onClick={onClick ? partial(withIntersection, onClick) : () => {}}
      onPointerDown={
        onPointerDown ? partial(withIntersection, onPointerDown) : () => {}
      }
      onPointerUp={
        onPointerUp ? partial(withIntersection, onPointerUp) : () => {}
      }
      onPointerOut={
        onPointerOut ? partial(withIntersection, onPointerOut) : () => {}
      }
      onPointerOver={
        onPointerOver ? partial(withIntersection, onPointerOver) : () => {}
      }
      onPointerEnter={
        onPointerEnter ? partial(withIntersection, onPointerEnter) : () => {}
      }
      onDoubleClick={
        onDoubleClick ? partial(withIntersection, onDoubleClick) : () => {}
      }
      visible={false}
      position={[0, 0, 0]}
      name="MouseHandler"
    >
      <planeGeometry args={[1000, 1000]} />
      <meshBasicMaterial transparent opacity={0} />
    </mesh>
  );
};

export default ToolPcdCanvasMouseHandler;
